import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Modal, StatusBadge, Empty } from "../components/ui";
import { useNavigate } from "react-router-dom";
import { Eye, Plus, Pause, Play, Trash2, KeyRound, Activity, Users, Building2, Shield, Copy, FolderOpen } from "lucide-react";

export default function SuperAdmin() {
  const [tab, setTab] = useState("tenants");
  return (
    <div data-testid="superadmin">
      <div className="flex items-center justify-between mb-4">
        <h1 className="card-h1" style={{ margin: 0 }}>Panel Super Administración</h1>
      </div>
      <Metrics />
      <div className="tabs-bar mt-4">
        <button className={`tab ${tab === "tenants" ? "active" : ""}`} onClick={() => setTab("tenants")} data-testid="tab-tenants"><Building2 size={14} /> Tenants</button>
        <button className={`tab ${tab === "users" ? "active" : ""}`} onClick={() => setTab("users")} data-testid="tab-global-users"><Users size={14} /> Usuarios Globales</button>
        <button className={`tab ${tab === "permissions" ? "active" : ""}`} onClick={() => setTab("permissions")} data-testid="tab-permissions"><Shield size={14} /> Permisos por Rol</button>
        <button className={`tab ${tab === "activity" ? "active" : ""}`} onClick={() => setTab("activity")} data-testid="tab-activity"><Activity size={14} /> Monitor de Actividad</button>
      </div>
      {tab === "tenants" && <TenantsTab />}
      {tab === "users" && <UsersTab />}
      {tab === "permissions" && <PermissionsTab />}
      {tab === "activity" && <ActivityTab />}
    </div>
  );
}

function Metrics() {
  const [m, setM] = useState(null);
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/metrics"); setM(data); } catch (e) { console.error(e); } })(); }, []);
  if (!m) return null;
  return (
    <div className="grid grid-cols-4 gap-3">
      <div className="kpi-card"><div className="label">Tenants totales</div><div className="value">{m.total_tenants}</div><div className="sub">{m.active_tenants} activos · {m.pending_tenants} pendientes</div></div>
      <div className="kpi-card"><div className="label">Usuarios</div><div className="value">{m.total_users}</div><div className="sub">Globales</div></div>
      <div className="kpi-card"><div className="label">Documentos</div><div className="value">{m.total_documents}</div><div className="sub">Registros</div></div>
      <div className="kpi-card"><div className="label">Logs de actividad</div><div className="value">{m.total_audit_logs}</div><div className="sub">Acumulados</div></div>
    </div>
  );
}

function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", installation_name: "", plan: "Standard" });
  const { enterAudit } = useAuth();
  const navigate = useNavigate();

  async function load() { try { const { data } = await api.get("/admin/tenants"); setTenants(data); } catch (e) { console.error(e); } }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!form.name) return;
    await api.post("/admin/tenants", form);
    setShowNew(false); setForm({ name: "", installation_name: "", plan: "Standard" });
    load();
  }

  async function suspend(id) { await api.post(`/admin/tenants/${id}/suspend`); load(); }
  async function activate(id) { await api.post(`/admin/tenants/${id}/activate`); load(); }
  async function delT(id) {
    if (!window.confirm("¿Eliminar tenant? Se hará backup automático.")) return;
    if (!window.confirm("Confirma una vez más esta acción crítica")) return;
    await api.delete(`/admin/tenants/${id}`); load();
  }
  async function enter(id) { await enterAudit(id); navigate("/dashboard"); }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)} data-testid="add-tenant"><Plus size={14} /> Nuevo Tenant</button>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Nombre</th><th>Plan</th><th>Status</th><th>Usuarios</th><th>ICA</th><th>Creado</th><th>Última actividad</th><th style={{ textAlign: "right" }}>Acciones</th></tr></thead>
          <tbody>
            {!tenants.length ? <tr><td colSpan={8}><Empty title="Sin tenants" sub="Crea el primero." /></td></tr> : tenants.map((t) => (
              <tr key={t.id}>
                <td><b>{t.name}</b><br /><span className="text-xs text-dim">{t.installation_name}</span></td>
                <td><span className="badge badge-blue">{t.plan}</span></td>
                <td><StatusBadge value={t.status === "active" ? "Activo" : t.status === "suspended" ? "Suspendido" : "Pendiente"} /></td>
                <td>{t.user_count}</td>
                <td><span className={`badge ${(t.ecs_score || 0) >= 85 ? "badge-green" : (t.ecs_score || 0) >= 60 ? "badge-yellow" : "badge-red"}`}>{t.ecs_score || 0}%</span></td>
                <td className="text-xs">{new Date(t.created_at).toLocaleDateString("es-MX")}</td>
                <td className="text-xs">
                  <ActivityDot ts={t.last_activity} />
                </td>
                <td style={{ textAlign: "right" }}>
                  <div className="flex gap-1 justify-end">
                    <button className="btn btn-sm" onClick={() => enter(t.id)} title="Entrar como auditor" data-testid={`audit-tenant-${t.id}`}><Eye size={12} /></button>
                    {t.status === "active" ? (
                      <button className="btn btn-sm" onClick={() => suspend(t.id)} title="Suspender" data-testid={`suspend-${t.id}`}><Pause size={12} /></button>
                    ) : (
                      <button className="btn btn-sm" onClick={() => activate(t.id)} title="Activar" data-testid={`activate-${t.id}`}><Play size={12} /></button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => delT(t.id)} title="Eliminar" data-testid={`del-tenant-${t.id}`}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="Nuevo Tenant" testId="modal-new-tenant">
        <div className="grid gap-3">
          <div><label className="label">Nombre *</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name" /></div>
          <div><label className="label">Nombre de instalación</label><input className="input" value={form.installation_name} onChange={(e) => setForm({ ...form, installation_name: e.target.value })} /></div>
          <div><label className="label">Plan</label>
            <select className="select" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              <option>Free</option><option>Standard</option><option>Pro</option><option>Enterprise</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button className="btn" onClick={() => setShowNew(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={create} data-testid="create-tenant">Crear</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ActivityDot({ ts }) {
  if (!ts) return <span className="badge badge-red">Sin actividad</span>;
  const hours = (Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60);
  if (hours < 24) return <span className="badge badge-green">Activo ahora</span>;
  if (hours < 24 * 7) return <span className="badge badge-yellow">Últimos 7 días</span>;
  return <span className="badge badge-red">+7 días sin actividad</span>;
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filter, setFilter] = useState({ tenant_id: "", role: "", status: "" });
  const [showNew, setShowNew] = useState(false);
  const [credsModal, setCredsModal] = useState(null);
  const [activityFor, setActivityFor] = useState(null);

  async function load() {
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) { console.error(e); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/tenants"); setTenants(data); })(); }, []);

  async function suspend(id) { await api.post(`/admin/users/${id}/suspend`); load(); }
  async function activate(id) { await api.post(`/admin/users/${id}/activate`); load(); }
  async function deleteUser(id, email) {
    if (!window.confirm(`¿Eliminar permanentemente a ${email}? Esta acción NO se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      load();
    } catch (e) { alert("No se pudo eliminar: " + (e.response?.data?.detail || e.message)); }
  }
  async function resetPw(id) {
    const pw = window.prompt("Nueva contraseña (mín. 4 caracteres):");
    if (!pw) return;
    await api.post(`/admin/users/${id}/reset-password`, { new_password: pw });
    alert("Contraseña reseteada.");
  }

  const allRoles = ["OPERARIO", "SUPERVISOR", "COORDINADOR", "JEFE", "SUPERINTENDENTE", "ADMIN", "SUPER_ADMIN", "RESPONSABLE", "GERENCIA", "EXTERNO"];

  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <select className="select" style={{ width: 220 }} value={filter.tenant_id} onChange={(e) => setFilter({ ...filter, tenant_id: e.target.value })} data-testid="filter-tenant">
          <option value="">Todos los tenants</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="select" style={{ width: 200 }} value={filter.role} onChange={(e) => setFilter({ ...filter, role: e.target.value })}>
          <option value="">Todos los roles</option>
          {allRoles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="select" style={{ width: 160 }} value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">Todos los status</option>
          <option value="active">Activo</option><option value="suspended">Suspendido</option>
        </select>
        <button className="btn btn-primary ml-auto" onClick={() => setShowNew(true)} data-testid="btn-new-user">
          <Plus size={14} /> Nuevo usuario
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Nombre</th><th>Email / Usuario</th><th>Rol</th><th>Tenant</th><th>Status</th><th>Último acceso</th><th style={{ textAlign: "right" }}>Acciones</th></tr></thead>
          <tbody>
            {!users.length ? <tr><td colSpan={7}><Empty /></td></tr> : users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td><b>{u.name}</b></td>
                <td className="text-xs">{u.email}{u.username && ` · @${u.username}`}</td>
                <td><span className="badge badge-blue">{u.role}</span></td>
                <td className="text-xs">{u.tenant_name || "-"}</td>
                <td><StatusBadge value={u.status === "active" ? "Activo" : "Suspendido"} /></td>
                <td className="text-xs">{u.last_login ? new Date(u.last_login).toLocaleDateString("es-MX") : "-"}</td>
                <td style={{ textAlign: "right" }}>
                  <div className="flex gap-1 justify-end">
                    <button className="btn btn-sm" onClick={() => setActivityFor(u)} title="Ver actividad y archivos" data-testid={`view-activity-${u.id}`}><Eye size={12} /></button>
                    <button className="btn btn-sm" onClick={() => resetPw(u.id)} title="Reset password" data-testid={`reset-pw-${u.id}`}><KeyRound size={12} /></button>
                    {u.status === "active" ? (
                      <button className="btn btn-sm" onClick={() => suspend(u.id)} title="Suspender" data-testid={`suspend-${u.id}`}><Pause size={12} /></button>
                    ) : (
                      <button className="btn btn-sm" onClick={() => activate(u.id)} title="Activar" data-testid={`activate-${u.id}`}><Play size={12} /></button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => deleteUser(u.id, u.email)} title="Eliminar" data-testid={`delete-${u.id}`}><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewUserModal
          tenants={tenants}
          allRoles={allRoles}
          onClose={() => setShowNew(false)}
          onCreated={(creds) => { setShowNew(false); setCredsModal(creds); load(); }}
        />
      )}

      {credsModal && <CredentialsModal creds={credsModal} onClose={() => setCredsModal(null)} />}
      {activityFor && <UserActivityModal user={activityFor} onClose={() => setActivityFor(null)} />}
    </div>
  );
}

function UserActivityModal({ user, onClose }) {
  const [activity, setActivity] = useState(null);
  const [files, setFiles] = useState(null);
  const [tab, setTab] = useState("activity");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, f] = await Promise.all([
          api.get(`/admin/users/${user.id}/activity`),
          api.get(`/admin/users/${user.id}/files`),
        ]);
        setActivity(a.data); setFiles(f.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user.id]);

  return (
    <Modal open onClose={onClose} title={`Comportamiento — ${user.name || user.email}`} size="xl" testId="modal-user-activity">
      <div className="mb-3">
        <div className="text-xs text-dim">
          <b>Email:</b> {user.email} · <b>Rol:</b> {user.role} · <b>Tenant:</b> {user.tenant_name || "—"} · <b>Status:</b> {user.status}
        </div>
      </div>
      <div className="tabs-bar">
        <button className={`tab ${tab === "activity" ? "active" : ""}`} onClick={() => setTab("activity")} data-testid="user-activity-tab-events">
          <Activity size={14} /> Actividad ({activity?.total ?? 0})
        </button>
        <button className={`tab ${tab === "files" ? "active" : ""}`} onClick={() => setTab("files")} data-testid="user-activity-tab-files">
          <FolderOpen size={14} /> Archivos ({files?.total ?? 0})
        </button>
      </div>
      {loading ? (
        <div className="text-dim p-4">Cargando…</div>
      ) : tab === "activity" ? (
        <div className="table-wrap mt-3" data-testid="user-activity-events">
          <table className="table">
            <thead><tr><th>Fecha</th><th>Acción</th><th>Módulo</th><th>Objetivo</th><th>IP</th></tr></thead>
            <tbody>
              {!activity?.events?.length ? <tr><td colSpan={5} className="text-dim" style={{ textAlign: "center", padding: 16 }}>Sin eventos.</td></tr> :
                activity.events.map((e, i) => (
                  <tr key={i}>
                    <td className="text-xs">{new Date(e.created_at).toLocaleString("es-MX")}</td>
                    <td><span className="badge badge-blue">{e.action}</span></td>
                    <td className="text-xs">{e.module || "—"}</td>
                    <td className="text-xs">{e.target_name || e.target_id || "—"}</td>
                    <td className="text-xs">{e.ip || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap mt-3" data-testid="user-activity-files">
          <table className="table">
            <thead><tr><th>Archivo</th><th>Módulo</th><th>Tamaño</th><th>Subido</th><th>Acción</th></tr></thead>
            <tbody>
              {!files?.files?.length ? <tr><td colSpan={5} className="text-dim" style={{ textAlign: "center", padding: 16 }}>Sin archivos.</td></tr> :
                files.files.map((f) => (
                  <tr key={f.id}>
                    <td><b className="text-sm">{f.name || f.original_name}</b>{f.folder && <span className="text-xs text-dim"> · 📁 {f.folder}</span>}</td>
                    <td><span className="badge">{f.module}</span></td>
                    <td className="text-xs">{((f.size || 0) / 1024).toFixed(1)} KB</td>
                    <td className="text-xs">{new Date(f.created_at).toLocaleDateString("es-MX")}</td>
                    <td>
                      <a className="btn btn-sm" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/view/${f.id}`} target="_blank" rel="noreferrer">Ver</a>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex justify-end mt-3">
        <button className="btn" onClick={onClose}>Cerrar</button>
      </div>
    </Modal>
  );
}

function NewUserModal({ tenants, allRoles, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", username: "", password: "", role: "OPERARIO", tenant_id: "", notify_email: "",
  });
  const [busy, setBusy] = useState(false);

  function genPassword() {
    const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
    let pw = "";
    for (let i = 0; i < 10; i++) pw += charset[Math.floor(Math.random() * charset.length)];
    setForm((f) => ({ ...f, password: pw }));
  }

  async function submit() {
    if (!form.email || !form.password || !form.role) {
      alert("Email, contraseña y rol son obligatorios.");
      return;
    }
    if (form.role !== "SUPER_ADMIN" && !form.tenant_id) {
      alert("Selecciona un tenant para este usuario.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", {
        ...form,
        notify_email: form.notify_email || form.email,
      });
      onCreated(data.credentials_to_share);
    } catch (e) {
      alert("No se pudo crear: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title="Crear nuevo usuario" testId="modal-new-user">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Nombre completo</label>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Juan Pérez" data-testid="new-user-name" />
        </div>
        <div>
          <label className="label">Email *</label>
          <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} placeholder="usuario@empresa.com" data-testid="new-user-email" />
        </div>
        <div>
          <label className="label">Username (opcional)</label>
          <input className="input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} placeholder="jperez" data-testid="new-user-username" />
        </div>
        <div>
          <label className="label">Email para envío de credenciales</label>
          <input className="input" type="email" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value.toLowerCase() })} placeholder="(si vacío usa el email principal)" data-testid="new-user-notify-email" />
        </div>
        <div>
          <label className="label">Contraseña *</label>
          <div className="flex gap-2">
            <input className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mín. 4 caracteres" data-testid="new-user-password" />
            <button className="btn btn-sm" type="button" onClick={genPassword} data-testid="new-user-gen-password">Generar</button>
          </div>
        </div>
        <div>
          <label className="label">Rol *</label>
          <select className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
            {allRoles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Tenant {form.role !== "SUPER_ADMIN" && "*"}</label>
          <select className="select" value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} disabled={form.role === "SUPER_ADMIN"} data-testid="new-user-tenant">
            <option value="">— Selecciona un tenant —</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>
      <div className="card mt-3" style={{ background: "rgba(245,184,46,0.08)", border: "1px solid rgba(245,184,46,0.3)" }}>
        <div className="text-xs">
          <b>Nota:</b> El envío de credenciales por correo está actualmente <b>MOCKEADO</b>.
          Al crear el usuario, te mostraremos las credenciales en pantalla para que tú las compartas manualmente.
        </div>
      </div>
      <div className="flex gap-2 justify-end mt-3">
        <button className="btn" onClick={onClose} disabled={busy}>Cancelar</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="new-user-submit">
          {busy ? <span className="spinner" /> : "Crear usuario"}
        </button>
      </div>
    </Modal>
  );
}

function CredentialsModal({ creds, onClose }) {
  function copyAll() {
    const text = `SGA Xerome — Credenciales de acceso
URL: ${window.location.origin}
Email: ${creds.email}
${creds.username ? `Username: ${creds.username}\n` : ""}Contraseña: ${creds.password}`;
    navigator.clipboard.writeText(text);
    alert("Credenciales copiadas al portapapeles.");
  }
  return (
    <Modal open onClose={onClose} title="✅ Usuario creado — Credenciales para compartir" testId="modal-credentials">
      <div className="card" style={{ background: "rgba(46,186,126,0.08)", border: "1px solid rgba(46,186,126,0.35)" }}>
        <div className="text-xs text-dim mb-3">{creds.delivery}</div>
        <div className="grid gap-2" style={{ fontFamily: "monospace", fontSize: 13 }}>
          <div><b>Email:</b> {creds.email}</div>
          {creds.username && <div><b>Username:</b> {creds.username}</div>}
          <div><b>Contraseña:</b> {creds.password}</div>
          <div><b>Notificar a:</b> {creds.notify_email}</div>
        </div>
      </div>
      <div className="flex gap-2 justify-end mt-3">
        <button className="btn" onClick={copyAll} data-testid="creds-copy"><Copy size={12} /> Copiar todo</button>
        <button className="btn btn-primary" onClick={onClose} data-testid="creds-close">Listo</button>
      </div>
    </Modal>
  );
}

function PermissionsTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  async function load() {
    try { const r = await api.get("/admin/permissions"); setData(r.data); }
    catch (e) { console.error(e); }
  }
  useEffect(() => { load(); }, []);

  async function toggle(role, action) {
    if (role === "SUPER_ADMIN") return;
    const m = data.matrix.find((r) => r.role === role);
    const updated = { ...m.actions, [action]: !m.actions[action] };
    setSaving(`${role}-${action}`);
    try {
      await api.put(`/admin/permissions/${role}`, { actions: updated });
      m.actions = updated;
      setData({ ...data });
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(null); }
  }

  if (!data) return <div className="text-dim">Cargando permisos…</div>;

  return (
    <div data-testid="permissions-matrix">
      <div className="card mb-3" style={{ background: "rgba(74,144,226,0.08)", border: "1px solid rgba(74,144,226,0.3)" }}>
        <div className="card-title">Matriz de Permisos por Rol</div>
        <p className="text-xs text-dim">
          Define qué acciones puede ejecutar cada rol en el sistema. Los cambios son inmediatos.
          <br /><b>read</b>: ver y descargar reportes · <b>create</b>: crear registros · <b>update</b>: editar (incluido registros de otros) · <b>delete</b>: eliminar · <b>export</b>: exportar a PDF/PPT/Excel.
          <br />Recuerda: <b>cualquier usuario puede editar y eliminar SUS PROPIOS registros</b> aunque el rol no tenga update/delete. La restricción aplica para los registros de otros usuarios.
          <br /><b>SUPER_ADMIN</b> siempre tiene control total y no se puede modificar.
        </p>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 220 }}>Rol</th>
              {data.actions.map((a) => <th key={a} style={{ textAlign: "center", textTransform: "uppercase" }}>{a}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.matrix.map((r) => (
              <tr key={r.role} data-testid={`perm-row-${r.role}`}>
                <td>
                  <b>{r.role}</b>
                  {r.is_legacy && <span className="badge badge-yellow ml-2" style={{ fontSize: 9 }}>legacy</span>}
                </td>
                {data.actions.map((a) => (
                  <td key={a} style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={r.actions[a]}
                      onChange={() => toggle(r.role, a)}
                      disabled={r.role === "SUPER_ADMIN" || saving === `${r.role}-${a}`}
                      data-testid={`perm-${r.role}-${a}`}
                      style={{ width: 18, height: 18, cursor: r.role === "SUPER_ADMIN" ? "not-allowed" : "pointer", accentColor: "var(--accent)" }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState({ tenant_id: "", module: "", action: "" });
  const [tenants, setTenants] = useState([]);

  async function load() {
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/admin/activity", { params });
    setLogs(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/tenants"); setTenants(data); })(); }, []);

  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <select className="select" style={{ width: 220 }} value={filter.tenant_id} onChange={(e) => setFilter({ ...filter, tenant_id: e.target.value })}>
          <option value="">Todos los tenants</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input className="input" placeholder="Módulo" style={{ width: 180 }} value={filter.module} onChange={(e) => setFilter({ ...filter, module: e.target.value })} />
        <input className="input" placeholder="Acción (create, update, login...)" style={{ width: 240 }} value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })} />
        <button className="btn btn-sm" onClick={load}>Filtrar</button>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Fecha</th><th>Usuario</th><th>Tenant</th><th>Módulo</th><th>Acción</th><th>Objetivo</th><th>IP</th></tr></thead>
          <tbody>
            {!logs.length ? <tr><td colSpan={7}><Empty title="Sin actividad reciente" /></td></tr> : logs.map((l) => (
              <tr key={l.id}>
                <td className="text-xs">{new Date(l.created_at).toLocaleString("es-MX")}</td>
                <td className="text-xs">{l.username || "-"}<br /><span className="text-dim">{l.role}</span></td>
                <td className="text-xs">{l.tenant_id ? l.tenant_id.slice(0, 8) : "-"}</td>
                <td className="text-xs">{l.module}</td>
                <td><span className="badge badge-blue">{l.action}</span></td>
                <td className="text-xs">{l.target_name || l.target_id || "-"}</td>
                <td className="text-xs text-dim">{l.ip || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
